'use client'

import { useState, type ComponentProps } from 'react'
import Image, { type ImageProps } from 'next/image'
import { StructuredText } from 'react-datocms'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import classNames from 'classnames'
import ImageLightbox from '../image-lightbox'
import s from './style.module.css'

interface BlogImageProps {
	image: ImageProps
	enableLightbox?: boolean
	caption?: ComponentProps<typeof StructuredText>['data']
	imagePosition?: 'center' | 'left' | 'right'
	size?: 'full' | 'half' | 'third'
	priority?: boolean
}

export default function BlogImage({
	image,
	enableLightbox,
	caption,
	imagePosition = 'center',
	size = 'full',
	priority,
}: BlogImageProps) {
	return (
		<div
			className={classNames(
				s.blogImage,
				s[size],
				s[imagePosition],
				caption && s.withCaption
			)}
		>
			{enableLightbox ? (
				<LightboxImage priority={priority} image={image} />
			) : (
				<InlineImage priority={priority} image={image} />
			)}

			{caption ? (
				<div className={s.caption}>
					<StructuredText data={caption} />
				</div>
			) : null}
		</div>
	)
}

function InlineImage({
	image,
	priority,
}: Pick<BlogImageProps, 'image' | 'priority'>) {
	return (
		<div className={s.image}>
			<Image
				src={image.src}
				alt={image.alt}
				width={image.width}
				height={image.height}
				priority={priority}
			/>
		</div>
	)
}

function LightboxImage({
	image,
	priority,
}: Pick<BlogImageProps, 'image' | 'priority'>) {
	const [lightboxOpened, setLightboxOpened] = useState(false)

	return (
		<>
			<div className={s.image}>
				<Image
					src={image.src}
					alt={image.alt}
					width={image.width}
					height={image.height}
					priority={priority}
				/>
				<button
					onClick={() => {
						setLightboxOpened(true)
					}}
					className={s.lightboxButton}
				>
					<VisuallyHidden>Open image in lightbox</VisuallyHidden>
				</button>
			</div>

			{lightboxOpened ? (
				<ImageLightbox
					onDismiss={() => {
						setLightboxOpened(false)
					}}
					image={image}
				/>
			) : null}
		</>
	)
}
